import { useState } from "react";

import AddTaskRoundedIcon from "@mui/icons-material/AddTaskRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import NoteAddRoundedIcon from "@mui/icons-material/NoteAddRounded";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import TranslateRoundedIcon from "@mui/icons-material/TranslateRounded";
import { Button, CircularProgress } from "@mui/material";

import { CaseNoteResponse, ClientDetailResponse } from "@/api";
import { AssignmentCreationDialog } from "@/common/components/assignments/assignment-creation/AssignmentCreationDialog";
import { useTranslation } from "@/translations";

import { CaseNoteCreationDialog } from "../case-note-creation/CaseNoteCreationDialog";
import { isEdictCaseNote } from "./CaseNoteActionButtons.utils";
import { OpenSrvButton } from "./OpenSrvButton";
import { useCaseNoteArchiveDownload } from "./hooks/useCaseNoteArchiveDownload";
import { useCaseNotePrint } from "./hooks/useCaseNotePrint";
import { useTranslationToolUrl } from "./hooks/useTranslationToolUrl";

interface Props {
  client: ClientDetailResponse;
  selectedCaseNote: CaseNoteResponse | null;
}

export function CaseNoteActionButtons({ client, selectedCaseNote }: Props) {
  const { t } = useTranslation();

  const [isCreationDialogOpen, setIsCreationDialogOpen] = useState(false);
  const [isAssignmentDialogOpen, setIsAssignmentDialogOpen] = useState(false);

  const translationToolUrl = useTranslationToolUrl();
  const { downloadArchive, isDownloading } = useCaseNoteArchiveDownload(client.clientId);
  const { print, isPrinting } = useCaseNotePrint(client.clientId, selectedCaseNote);

  return (
    <>
      {translationToolUrl && (
        <Button
          variant="outlined"
          size="small"
          href={translationToolUrl}
          target="_blank"
          rel="noopener noreferrer"
          startIcon={<TranslateRoundedIcon />}
        >
          {t("clients.client.caseNotes.translationTool")}
        </Button>
      )}
      {isEdictCaseNote(selectedCaseNote) && (
        <OpenSrvButton
          key={selectedCaseNote.caseNoteId}
          clientId={client.clientId}
          caseNoteId={selectedCaseNote.caseNoteId}
        />
      )}
      <Button
        variant="outlined"
        size="small"
        onClick={downloadArchive}
        disabled={isDownloading}
        aria-busy={isDownloading}
        startIcon={isDownloading ? <CircularProgress size={16} color="inherit" aria-hidden /> : <DownloadRoundedIcon />}
      >
        {t(
          isDownloading ? "clients.client.caseNotes.downloadingCaseFile" : "clients.client.caseNotes.downloadCaseFile"
        )}
      </Button>
      <Button
        variant="outlined"
        size="small"
        disabled={!selectedCaseNote || isPrinting}
        onClick={() => void print()}
        startIcon={<PrintRoundedIcon />}
      >
        {t("common.print")}
      </Button>
      <Button
        variant="outlined"
        size="small"
        disabled={!selectedCaseNote}
        onClick={() => setIsAssignmentDialogOpen(true)}
        startIcon={<AddTaskRoundedIcon />}
      >
        {t("clients.client.caseNotes.createAvAssignment")}
      </Button>
      <Button
        variant="contained"
        size="small"
        onClick={() => setIsCreationDialogOpen(true)}
        startIcon={<NoteAddRoundedIcon />}
      >
        {t("clients.client.caseNotes.createNote")}
      </Button>
      {isCreationDialogOpen && (
        <CaseNoteCreationDialog client={client} onClose={() => setIsCreationDialogOpen(false)} />
      )}
      {isAssignmentDialogOpen && selectedCaseNote && (
        <AssignmentCreationDialog
          client={client}
          caseNote={selectedCaseNote}
          onClose={() => setIsAssignmentDialogOpen(false)}
        />
      )}
    </>
  );
}
